import { useSubmit } from "@hyper-fetch/react";
import { ArrowRight, Loader2 } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { toast } from "sonner";

import { sdk } from "@/api/sdk";
import { NewsletterHero } from "@/features/newsletter/components/newsletter-hero";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

export interface NewsletterHeroSignupProps {
  /** Attribution tag stored with the signup and shown in the admin subscriber list. */
  source?: string;
  /** Override the headline (e.g. the archive's own name). */
  title?: string;
  /** Override the supporting line. */
  description?: string;
  className?: string;
}

const fade = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.22, ease: [0.22, 1, 0.36, 1] as const },
};

/**
 * The public twin of `NewsletterMembership`: the same envelope-hero card, but collecting an email
 * address, since a public page cannot assume a session. This is what headlines the hosted
 * archive. A successful signup runs the same ceremony (envelope hop, sparkle burst, wax seal) and
 * morphs the copy to the "on the list" state with a reset for adding another address. The
 * one-tap membership card ships alongside, unmounted, for surfaces where the app knows the user.
 */
export function NewsletterHeroSignup({ source = "archive", title, description, className }: NewsletterHeroSignupProps) {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  // Nonzero only right after a signup lands; keys the hero's one-shot burst.
  const [celebrate, setCelebrate] = useState(0);

  const { submit: subscribe, submitting, onSubmitSuccess, onSubmitError } = useSubmit(sdk.newsletter.subscribe.$post);

  onSubmitSuccess(() => {
    setSubscribed(true);
    setCelebrate(Date.now());
    setEmail("");
  });
  onSubmitError((error) => {
    toast.error((error as { message?: string }).message ?? m.newsletter_signup_error());
  });

  return (
    <section
      className={cn("bg-card/40 relative w-full overflow-hidden rounded-3xl border", className)}
      data-tour="newsletter-hero-signup"
    >
      <NewsletterHero subscribed={subscribed} celebrate={celebrate} />

      <div className="mx-auto flex w-full max-w-md flex-col items-center px-6 pb-10 text-center">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div key={subscribed ? "done" : "signup"} {...fade} className="flex w-full flex-col items-center">
            <h2 className="text-balance text-2xl font-bold tracking-tight">
              {subscribed ? m.newsletter_member_subscribed_title() : (title ?? m.newsletter_member_title())}
            </h2>
            <p className="text-muted-foreground mt-2 text-balance text-sm leading-6">
              {subscribed ? m.newsletter_member_subscribed_description() : (description ?? m.newsletter_member_description())}
            </p>

            {subscribed ? (
              <button
                type="button"
                onClick={() => setSubscribed(false)}
                className="text-muted-foreground hover:text-foreground mt-6 text-xs font-medium underline-offset-2 transition-colors hover:underline"
              >
                {m.newsletter_signup_again()}
              </button>
            ) : (
              <form
                className={cn(
                  "bg-background/90 mt-6 flex h-12 w-full items-center gap-2 rounded-2xl border p-1.5 pl-4 shadow-sm backdrop-blur transition-all",
                  "hover:border-primary/30 focus-within:border-primary/50 focus-within:ring-primary/10 focus-within:ring-4",
                )}
                onSubmit={(event) => {
                  event.preventDefault();
                  if (!email.trim() || submitting) return;
                  subscribe({ payload: { email: email.trim(), source } });
                }}
              >
                <input
                  id="newsletter-hero-email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder={m.newsletter_signup_placeholder()}
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="placeholder:text-muted-foreground/60 min-w-0 flex-1 bg-transparent text-sm outline-none"
                />
                <button
                  type="submit"
                  disabled={submitting || !email.trim()}
                  className={cn(
                    "bg-primary text-primary-foreground flex h-full shrink-0 items-center gap-1.5 rounded-xl px-4 text-sm font-semibold",
                    "transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-35",
                  )}
                >
                  {submitting ? <Loader2 className="size-4 animate-spin" /> : m.newsletter_signup_submit()}
                  {submitting ? null : <ArrowRight className="size-4" />}
                </button>
              </form>
            )}
          </motion.div>
        </AnimatePresence>

        <p className="text-muted-foreground/80 mt-3 text-xs">{m.newsletter_card_privacy_note()}</p>
      </div>
    </section>
  );
}
